/**
 * デバッグログユーティリティ
 *
 * 使用方法:
 *   import { debug } from '../utils/debug';
 *   debug.log('message');      // DEBUG=true の時のみ出力
 *   debug.warn('warning');
 *   debug.error('error');      // エラーは常に出力
 *
 * 環境変数または localStorage で制御:
 *   - 環境変数: VITE_DEBUG=true
 *   - localStorage: localStorage.setItem('DEBUG', 'true')
 *   - ブラウザコンソール: window.enableDebug() / window.disableDebug()
 */

// デバッグフラグの判定
function isDebugEnabled(): boolean {
  // 環境変数チェック（Vite環境）
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_DEBUG === 'true') {
      return true;
    }
  } catch {
    // import.meta が使えない環境（Node.jsなど）では無視
  }

  // localStorage チェック（ブラウザ環境のみ）
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    return localStorage.getItem('DEBUG') === 'true';
  }

  // テスト環境ではデフォルトで無効
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    return process.env.DEBUG === 'true';
  }

  return false;
}

// キャッシュされたデバッグ状態（パフォーマンス用）
let cachedDebugState: boolean | null = null;

function getDebugState(): boolean {
  if (cachedDebugState === null) {
    cachedDebugState = isDebugEnabled();
  }
  return cachedDebugState;
}

// デバッグ状態をリセット（テスト用）
export function resetDebugCache(): void {
  cachedDebugState = null;
}

// デバッグログ関数
export const debug = {
  log: (...args: unknown[]): void => {
    if (getDebugState()) {
      console.log('[DEBUG]', ...args);
    }
  },

  warn: (...args: unknown[]): void => {
    if (getDebugState()) {
      console.warn('[DEBUG]', ...args);
    }
  },

  error: (...args: unknown[]): void => {
    // エラーは常に出力
    console.error('[ERROR]', ...args);
  },

  info: (...args: unknown[]): void => {
    if (getDebugState()) {
      console.info('[DEBUG]', ...args);
    }
  },

  // 特定のカテゴリ用のロガーを作成
  category: (category: string) => ({
    log: (...args: unknown[]): void => {
      if (getDebugState()) {
        console.log(`[${category}]`, ...args);
      }
    },
    warn: (...args: unknown[]): void => {
      if (getDebugState()) {
        console.warn(`[${category}]`, ...args);
      }
    },
    error: (...args: unknown[]): void => {
      console.error(`[${category}]`, ...args);
    },
  }),
};

// ブラウザ環境でのグローバルヘルパー
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).enableDebug = () => {
    localStorage.setItem('DEBUG', 'true');
    cachedDebugState = true;
    console.log('Debug mode enabled. Refresh may be needed for full effect.');
  };

  (window as unknown as Record<string, unknown>).disableDebug = () => {
    localStorage.removeItem('DEBUG');
    cachedDebugState = false;
    console.log('Debug mode disabled.');
  };
}

export default debug;
